/**
 * File dialog.js.
 *
 * Modal/dialog functionality.
 *
 * @package wildrivers
 */

import A11yDialog from 'a11y-dialog';
import { WPRest } from './wprest';


// https://www.sohamkamani.com/blog/javascript-localstorage-with-ttl-expiry/
function setWithExpiry(key, value, ttl) {
	const now = new Date()

	// `item` is an object which contains the original value
	// as well as the time when it's supposed to expire
	const item = {
		value: value,
		expiry: now.getTime() + ttl,
	}
	localStorage.setItem(key, JSON.stringify(item))
}

function getWithExpiry(key) {
	const itemStr = localStorage.getItem(key)
	// if the item doesn't exist, return null
	if (!itemStr) {
		return null
	}
	const item = JSON.parse(itemStr)
	const now = new Date()
	// compare the expiry time of the item with the current time
	if (now.getTime() > item.expiry) {
		// If the item is expired, delete the item from storage
		// and return null
		localStorage.removeItem(key)
		return null
	}
	return item.value
}

function activeA11yToggle() {
    const body = document.getElementsByTagName('body')[0];
    if ( body.classList.contains('a11y-dialog-open') ) {
        body.classList.remove('a11y-dialog-open');
    } else {
        body.classList.add('a11y-dialog-open');
    }
}

function convertToInt( value ) {
    const conversion = {
        none: 0,
        hours: 6 * 60 * 60,
        day: 24 * 60 * 60,
        days: 3 * 24 * 60 * 60,
        week: 7 * 24 * 60 * 60,
        weeks: 2 * 7 * 24 * 60 * 60,
    };

    return conversion[value];
}

export function dialog() {
    // search modal.
    const search = document.querySelector('#header-search');
    if ( search ) {
        const search_dialog = new A11yDialog(search);
        let opener          = null;

        search_dialog.on( 'show', (e) => {
            activeA11yToggle();
            opener = e.detail.target;
        });
        search_dialog.on( 'hide', () => {
            activeA11yToggle();
        });
    }

    // newsletter modal.
    const newsletter = document.querySelector( '#newsletter-modal' );
    if ( newsletter ) {
        const newsletterLinks = document.querySelectorAll( 'a[href="#!newsletter"]' );

        if ( newsletterLinks?.length ) {
            newsletterLinks.forEach( (link) => {
                link.setAttribute( 'data-a11y-dialog-show', 'newsletter-modal' );
            });
        }

        const newsletter_dialog = new A11yDialog( newsletter );
        let opener              = null;

        newsletter_dialog.on( 'show', (e) => {
            activeA11yToggle();
            opener = e.detail.target;
        });
        newsletter_dialog.on( 'hide', () => {
            activeA11yToggle();
        });
    }

    // alert modal.
    const alert = document.querySelector( '#alert-modal' );
    if ( alert ) {
        const body         = document.getElementsByTagName('body')[0];
        const alert_dialog = new A11yDialog( alert );
        let opener         = null;

        // add auto-loading popups
        const activeAlerts = document.querySelector( '#wr_active_alerts' );
        if ( activeAlerts && activeAlerts?.value ) {
            const autoAlerts = JSON.parse( activeAlerts.value );
            
            autoAlerts?.forEach( (item) => {
                const alertPaused = getWithExpiry( 'alert-closed-' + item );
                if ( 'closed' === alertPaused ) {
                    return;
                }
                const popup = document.createElement('div');
                popup.classList.add( 'alert-modal-autoload' );
                popup.setAttribute( 'data-a11y-dialog-show', 'alert-modal' );
                popup.setAttribute( 'data-alert-id', item );
                body.appendChild( popup );
            });

            const loadedAlerts = document.querySelectorAll( '.alert-modal-autoload' );
            loadedAlerts?.forEach( (item, index) => {
                WPRest.get( '/wp-json/wp/v2/alert/' + item.dataset.alertId ).then( result => {
                    let delay = 5000;
                    if ( result.acf.visibility_settings.delay ) {
                        delay = result.acf.visibility_settings.delay;
                    }
                    setTimeout(
                        () => {
                            item.click();
                        },
                        parseInt(delay) * 1000
                    );
                });
            });
        }

        const alertTriggers = document.querySelectorAll( 'a[href*="#!alert-"]' );
        if ( alertTriggers?.length ) {
            alertTriggers.forEach( (link) => {
                link.setAttribute( 'data-a11y-dialog-show', 'alert-modal' );
                const alertId = link.href.search( /\#\!alert-([0-9]+)/ );
                if ( alertId > 0 ) {
                    link.setAttribute( 'data-alert-id', link.href.substr( alertId + 8 ) );
                }
            });
        }

        alert_dialog.on( 'show', (e) => {
            activeA11yToggle();
            const target = e.detail.target;
            const opener = target.closest('[data-a11y-dialog-show]');
            alert.dataset.alertId = opener.dataset.alertId;
            
            const image   = alert.querySelector( '.dialog-content' );
            const title   = alert.querySelector( '.wr-modal--title' );
            const details = alert.querySelector( '.wr-modal--details' );
            const button  = alert.querySelector( '.wr-modal--button' );

            WPRest.get( '/wp-json/wp/v2/alert/' + opener.dataset.alertId + '?_embed' ).then( result => {
                if ( result.featured_media ) {
                    const currentImage    = result._embedded['wp:featuredmedia']['0'];
                    const desktopImageSrc = currentImage.source_url;
                    image.setAttribute('style', 'background-image: url("' + desktopImageSrc + '");' );
                }

                if ( 'trigger' !== result.acf.visibility_settings.visibility ) {
                    let reopen = 604800000;
                    if ( result.acf.visibility_settings.reopen ) {
                        alert.dataset.reopen = convertToInt( result.acf.visibility_settings.reopen );
                    } else {
                        alert.dataset.reopen = reopen;
                    }
                }

                if ( result.acf.heading ) {
                    title.innerText = result.acf.heading;
                } else {
                    title.innerText = '';
                }

                if ( result.acf.text ) {
                    details.innerHTML = result.acf.text.replace( /\r\n/g, '<br>' );
                } else {
                    details.innerText = '';
                }

                if ( result.acf.button ) {
                    button.setAttribute( 'href', result.acf.button.url );
                    if ( result.acf.button.title ) {
                        button.innerText = result.acf.button.title;
                    } else {
                        button.innerText = 'Learn More';
                    }
                    if ( result.acf.button.target ) {
                        button.setAttribute( 'target', result.acf.button.target );
                    }
                    button.setAttribute( 'style', 'display: inline-block;' );
                }
            });
        });
        alert_dialog.on( 'hide', () => {
            activeA11yToggle();
            if ( alert.dataset.reopen ) {
                setWithExpiry( 'alert-closed-' + alert.dataset.alertId, 'closed', alert.dataset.reopen );
            }
            const image   = alert.querySelector( '.dialog-overlay' );
            const title   = alert.querySelector( '.wr-modal--title' );
            const details = alert.querySelector( '.wr-modal--details' );
            const button  = alert.querySelector( '.wr-modal--button' );

            image.setAttribute( 'style', '' );
            title.innerHTML = '<span class="placeholder loading"></span>';
            details.innerHTML = '<span class="placeholder loading"></span>';
            button.setAttribute( 'href', '#!' );
            button.innerText = "";
            button.setAttribute( 'target', '_self' );
            button.setAttribute( 'style', 'display:none;' );
        });
    }

    // person modal, visible anywhere there is a person modal block
    const person = document.querySelector('#person-modal');
    if ( person ) {
        
        // disable permalinks on people
        const peopleLinks = document.querySelectorAll( '.person-block--link, .author-block--link' );
        if ( peopleLinks?.length ) {
            peopleLinks.forEach( (link) => {
                link.addEventListener( 'click', (e) => {
                    e.preventDefault();
                });
            });
        }

        const people_dialog = new A11yDialog(person);

        people_dialog.on( 'show', function(event) {
            activeA11yToggle();
            const target = event.detail.target;
            const opener = target.closest('[data-a11y-dialog-show]');

            const image        = person.querySelector( '.wr-modal--image.desktop' );
            const imageMobile  = person.querySelector( '.wr-modal--image.mobile' );
            const name         = person.querySelector( '.wr-modal--title' );
            const position     = person.querySelector( '.wr-modal--position' );
            const expertise    = person.querySelector( '.wr-modal--expertise' );
            const email        = person.querySelector( '.wr-modal--email' );
            const emailName    = person.querySelector( '.wr-modal--email-name' );
            const bio          = person.querySelector( '.wr-modal--bio' );

            WPRest.get( '/wp-json/wp/v2/people/' + opener.dataset.personId + '?_embed' ).then( result => {

                if ( result.featured_media ) {
                    let desktopImageSrc    = '';
                    let desktopImageWidth  = '';
                    let desktopImageHeight = '';

                    if ( result._embedded['wp:featuredmedia']['0'].media_details.sizes['image-grid'] ) {
                        let currentImage = result._embedded['wp:featuredmedia']['0'].media_details.sizes['image-grid'];
                        desktopImageSrc    = currentImage.source_url;
                        desktopImageWidth  = currentImage.width;
                        desktopImageHeight = currentImage.height;
                    } else {
                        let currentImage = result._embedded['wp:featuredmedia']['0'];
                        desktopImageSrc    = currentImage.source_url;
                        desktopImageWidth  = currentImage.media_details.width;
                        desktopImageHeight = currentImage.media_details.height;
                    }

                    let mobileImageSrc    = '';
                    let mobileImageWidth  = '';
                    let mobileImageHeight = '';
                    if ( result._embedded['wp:featuredmedia']['0'].media_details.sizes.person ) {
                        let currentImage = result._embedded['wp:featuredmedia']['0'].media_details.sizes.person;
                        mobileImageSrc    = currentImage.source_url;
                        mobileImageWidth  = currentImage.width;
                        mobileImageHeight = currentImage.height;
                    } else {
                        let currentImage = result._embedded['wp:featuredmedia']['0'];
                        mobileImageSrc    = currentImage.source_url;
                        mobileImageWidth  = currentImage.media_details.width;
                        mobileImageHeight = currentImage.media_details.height;
                    }

                    image.setAttribute('src', desktopImageSrc );
                    image.setAttribute('width', desktopImageWidth );
                    image.setAttribute('height', desktopImageHeight );
                    image.setAttribute('alt', htmlDecode( result.title.rendered ) );
                    image.classList.remove('img-loading');
                    
                    imageMobile.setAttribute('src', mobileImageSrc );
                    imageMobile.setAttribute('width', mobileImageWidth );
                    imageMobile.setAttribute('height', mobileImageHeight );
                    imageMobile.setAttribute('alt', htmlDecode( result.title.rendered ) );
                    imageMobile.classList.remove('img-loading');
                }

                name.innerText = htmlDecode( result.title.rendered );

                if ( result.acf.role ) {
                    position.innerText = htmlDecode( result.acf.role );
                } else {
                    position.innerText = '';
                }

                if ( result.acf.topic_of_expertise ) {
                    expertise.innerText = htmlDecode( result.acf.topic_of_expertise );
                } else {
                    expertise.innerText = '';
                }

                if ( result.acf.email ) {
                    email.setAttribute( 'href', 'mailto:' + result.acf.email );
                    emailName.innerText = htmlDecode( result.title.rendered.split(' ')[0] );
                    email.setAttribute( 'style', 'display:inline-block;' );
                }

                bio.innerHTML = htmlDecode( result.content.rendered );

                this.dataset.loaded = true;
            });
        });

        people_dialog.on('hide', function (event) {
            activeA11yToggle();
            const image        = person.querySelector( '.wr-modal--image.desktop' );
            const imageMobile  = person.querySelector( '.wr-modal--image.mobile' );
            const name         = person.querySelector( '.wr-modal--title' );
            const position     = person.querySelector( '.wr-modal--position' );
            const expertise    = person.querySelector( '.wr-modal--expertise' );
            const email        = person.querySelector( '.wr-modal--email' );
            const emailName    = person.querySelector( '.wr-modal--email-name' );
            const bio          = person.querySelector( '.wr-modal--bio' );
            
            image.setAttribute('src', '' );
            image.setAttribute('width', '' );
            image.setAttribute('height', '' );
            image.setAttribute('alt', '' );
            image.classList.add('img-loading');

            imageMobile.setAttribute('src', '' );
            imageMobile.setAttribute('width', '' );
            imageMobile.setAttribute('height', '' );
            imageMobile.setAttribute('alt', '' );
            imageMobile.classList.add('img-loading');

            name.innerHTML = '<span class="placeholder loading"></span>';
            position.innerHTML = '<span class="placeholder loading"></span>';
            expertise.innerHTML = '<span class="placeholder loading"></span>';
            email.setAttribute( 'href', 'mailto:' );
            email.setAttribute( 'style', 'display:none;' );
            emailName.innerText = '';
            bio.innerHTML = '<span class="placeholder loading large"></span>';

            this.dataset.loaded = false;
        });
    }

    // program modal, visible with programs listings
    const program = document.querySelector('#program-modal');
    if ( program ) {
        
        // disable permalinks on programs
        const programLinks = document.querySelectorAll( '.program-block--link' );
        if ( programLinks ) {
            programLinks.forEach( (link) => {
                link.addEventListener( 'click', (e) => {
                    e.preventDefault();
                });
            });
        }
        
        const program_dialog = new A11yDialog(program);

        program_dialog.on( 'show', function(event) {
            activeA11yToggle();
            const target = event.detail.target;
            const opener = target.closest('[data-a11y-dialog-show]');

            const image        = program.querySelector( '.wr-modal--image.desktop' );
            const imageMobile  = program.querySelector( '.wr-modal--image.mobile' );
            const title        = program.querySelector( '.wr-modal--title' );
            const grades       = program.querySelector( '.wr-modal--grades' );
            const topics       = program.querySelector( '.wr-modal--topics' );
            const email        = program.querySelector( '.wr-modal--email' );
            const url          = program.querySelector( '.wr-modal--url' );
            const description  = program.querySelector( '.wr-modal--description' );
            const standards    = program.querySelector( '.wr-modal--standards' );
            const location     = program.querySelector( '.wr-modal--location' );
            const directions   = program.querySelector( '.wr-modal--directions' );

            WPRest.get( '/wp-json/wp/v2/program/' + opener.dataset.programId + '?_embed' ).then( result => {

                if ( result.featured_media ) {
                    let desktopImageSrc    = '';
                    let desktopImageWidth  = '';
                    let desktopImageHeight = '';

                    if ( result._embedded['wp:featuredmedia']['0'].media_details.sizes['image-grid'] ) {
                        let currentImage = result._embedded['wp:featuredmedia']['0'].media_details.sizes['image-grid'];
                        desktopImageSrc    = currentImage.source_url;
                        desktopImageWidth  = currentImage.width;
                        desktopImageHeight = currentImage.height;
                    } else {
                        let currentImage = result._embedded['wp:featuredmedia']['0'];
                        desktopImageSrc    = currentImage.source_url;
                        desktopImageWidth  = currentImage.media_details.width;
                        desktopImageHeight = currentImage.media_details.height;
                    }

                    let mobileImageSrc    = '';
                    let mobileImageWidth  = '';
                    let mobileImageHeight = '';
                    if ( result._embedded['wp:featuredmedia']['0'].media_details.sizes.people ) {
                        let currentImage = result._embedded['wp:featuredmedia']['0'].media_details.sizes.people;
                        mobileImageSrc    = currentImage.source_url;
                        mobileImageWidth  = currentImage.width;
                        mobileImageHeight = currentImage.height;
                    } else {
                        let currentImage = result._embedded['wp:featuredmedia']['0'];
                        mobileImageSrc    = currentImage.source_url;
                        mobileImageWidth  = currentImage.media_details.width;
                        mobileImageHeight = currentImage.media_details.height;
                    }

                    image.setAttribute('src', desktopImageSrc );
                    image.setAttribute('width', desktopImageWidth );
                    image.setAttribute('height', desktopImageHeight );
                    image.setAttribute('alt', htmlDecode( result.title.rendered ) );
                    image.classList.remove('img-loading');
                    
                    imageMobile.setAttribute('src', mobileImageSrc );
                    imageMobile.setAttribute('width', mobileImageWidth );
                    imageMobile.setAttribute('height', mobileImageHeight );
                    imageMobile.setAttribute('alt', htmlDecode( result.title.rendered ) );
                    imageMobile.classList.remove('img-loading');
                }
                
                title.innerText = htmlDecode( result.title.rendered );
                
                if ( opener.dataset.age ) {
                    grades.innerHTML = '<b>Grades: </b>' + htmlDecode( opener.dataset.age );
                } else {
                    grades.innerText = '';
                }
                
                if ( opener.dataset.category ) {
                    topics.innerHTML = '<b>Topics: </b>' + htmlDecode( opener.dataset.category );
                } else {
                    topics.innerText = '';
                }

                // url or email
                if ( result.acf.email ) {
                    email.setAttribute( 'href', 'mailto:' + result.acf.email );
                    email.setAttribute( 'style', 'display:inline-block;' );
                }
                if ( result.acf.program_url ) {
                    url.setAttribute( 'href', result.acf.program_url );
                    url.setAttribute( 'style', 'display:inline-block;' );
                }

                if ( result.acf.program_description ) {
                    description.innerHTML = '<h3>What Students Will Do</h3><div>' + result.acf.program_description + '</div>';
                } else {
                    description.innerText = '';
                }

                if ( result.acf.standards ) {
                    standards.innerHTML = '<h3>Standards Met</h3><div>' + result.acf.standards + '</div>';
                } else {
                    standards.innerText = '';
                }

                if ( result.acf.location ) {
                    location.innerHTML = '<h3>Location</h3><div>' + result.acf.location.replace( '\r\n', '<br>' ) + '</div>';
                    directions.setAttribute( 'href', 'https://www.google.com/maps/dir/' + htmlDecode( result.acf.location ).replace( ' ', '+' ) );
                    directions.setAttribute( 'style', 'display:inline-block;' );
                } else {
                    location.innerText = '';
                }
                
                this.dataset.loaded = true;
            });
        });

        program_dialog.on('hide', function (event) {
            activeA11yToggle();
            const image        = program.querySelector( '.wr-modal--image.desktop' );
            const imageMobile  = program.querySelector( '.wr-modal--image.mobile' );
            const title        = program.querySelector( '.wr-modal--title' );
            const grades       = program.querySelector( '.wr-modal--grades' );
            const topics       = program.querySelector( '.wr-modal--topics' );
            const email        = program.querySelector( '.wr-modal--email' );
            const url          = program.querySelector( '.wr-modal--url' );
            const description  = program.querySelector( '.wr-modal--description' );
            const standards    = program.querySelector( '.wr-modal--standards' );
            const location     = program.querySelector( '.wr-modal--location' );
            const directions   = program.querySelector( '.wr-modal--directions' );
            
            image.setAttribute('src', '' );
            image.setAttribute('width', '' );
            image.setAttribute('height', '' );
            image.setAttribute('alt', '' );
            image.classList.add('img-loading');

            imageMobile.setAttribute('src', '' );
            imageMobile.setAttribute('width', '' );
            imageMobile.setAttribute('height', '' );
            imageMobile.setAttribute('alt', '' );
            imageMobile.classList.add('img-loading');

            title.innerHTML = '<span class="placeholder loading"></span>';
            grades.innerHTML = '<span class="placeholder loading"></span>';
            topics.innerHTML = '<span class="placeholder loading"></span>';
            email.setAttribute( 'href', 'mailto:' );
            email.setAttribute( 'style', 'display:none;' );
            url.setAttribute( 'href', '#!' );
            url.setAttribute( 'style', 'display:none;' );
            description.innerHTML = '<span class="placeholder loading large"></span>';
            standards.innerHTML = '<span class="placeholder loading large"></span>';
            location.innerHTML = '<span class="placeholder loading large"></span>';
            directions.setAttribute( 'href', 'https://www.google.com/maps/dir/' );
            directions.setAttribute( 'style', 'display:none;' );

            this.dataset.loaded = false;
        });
    } 

    // opporunity modal, visible with opporunity listings
    const opportunity = document.querySelector('#opportunity-modal');
    if ( opportunity ) {
        
        // disable permalinks on opporunity
        const opportunityLinks = document.querySelectorAll( '.opportunity-block--link' );
        if ( opportunityLinks ) {
            opportunityLinks.forEach( (link) => {
                link.addEventListener( 'click', (e) => {
                    e.preventDefault();
                });
            });
        }
        
        const opportunity_dialog = new A11yDialog(opportunity);

        opportunity_dialog.on( 'show', function(event) {
            activeA11yToggle();
            const target = event.detail.target;
            const opener = target.closest('[data-a11y-dialog-show]');

            const image        = opportunity.querySelector( '.wr-modal--image.desktop' );
            const imageMobile  = opportunity.querySelector( '.wr-modal--image.mobile' );
            const title        = opportunity.querySelector( '.wr-modal--title' );
            const commitment   = opportunity.querySelector( '.wr-modal--commitment' );
            const deadline     = opportunity.querySelector( '.wr-modal--deadline' );
            const url          = opportunity.querySelector( '.wr-modal--url' );
            const description  = opportunity.querySelector( '.wr-modal--description' );
            const add_info     = opportunity.querySelector( '.wr-modal--additional-information' );
            const location     = opportunity.querySelector( '.wr-modal--location' );
            const directions   = opportunity.querySelector( '.wr-modal--directions' );

            WPRest.get( '/wp-json/wp/v2/opportunity/' + opener.dataset.opportunityId + '?_embed' ).then( result => {

                if ( result.featured_media ) {
                    let desktopImageSrc    = '';
                    let desktopImageWidth  = '';
                    let desktopImageHeight = '';

                    if ( result._embedded['wp:featuredmedia']['0'].media_details.sizes['image-grid'] ) {
                        let currentImage = result._embedded['wp:featuredmedia']['0'].media_details.sizes['image-grid'];
                        desktopImageSrc    = currentImage.source_url;
                        desktopImageWidth  = currentImage.width;
                        desktopImageHeight = currentImage.height;
                    } else {
                        let currentImage = result._embedded['wp:featuredmedia']['0'];
                        desktopImageSrc    = currentImage.source_url;
                        desktopImageWidth  = currentImage.media_details.width;
                        desktopImageHeight = currentImage.media_details.height;
                    }

                    let mobileImageSrc    = '';
                    let mobileImageWidth  = '';
                    let mobileImageHeight = '';
                    if ( result._embedded['wp:featuredmedia']['0'].media_details.sizes.people ) {
                        let currentImage = result._embedded['wp:featuredmedia']['0'].media_details.sizes.people;
                        mobileImageSrc    = currentImage.source_url;
                        mobileImageWidth  = currentImage.width;
                        mobileImageHeight = currentImage.height;
                    } else {
                        let currentImage = result._embedded['wp:featuredmedia']['0'];
                        mobileImageSrc    = currentImage.source_url;
                        mobileImageWidth  = currentImage.media_details.width;
                        mobileImageHeight = currentImage.media_details.height;
                    }

                    image.setAttribute('src', desktopImageSrc );
                    image.setAttribute('width', desktopImageWidth );
                    image.setAttribute('height', desktopImageHeight );
                    image.setAttribute('alt', htmlDecode( result.title.rendered ) );
                    image.classList.remove('img-loading');
                    
                    imageMobile.setAttribute('src', mobileImageSrc );
                    imageMobile.setAttribute('width', mobileImageWidth );
                    imageMobile.setAttribute('height', mobileImageHeight );
                    imageMobile.setAttribute('alt', htmlDecode( result.title.rendered ) );
                    imageMobile.classList.remove('img-loading');
                }
                
                title.innerText = htmlDecode( result.title.rendered );
                
                if ( opener.dataset.commitment ) {
                    commitment.innerHTML = '<b>Time Commitment: </b>' + htmlDecode( opener.dataset.commitment );
                } else {
                    commitment.innerText = '';
                }

                if ( result.acf.application_deadline ) {
                    const d = new Date( htmlDecode( result.acf.application_deadline ) );
                    // 5pm on Friday, Feb 6th 2026
                    const hours = d.getHours() < 1 ? '12' : d.getHours();
                    const am_pm = d.getHours() >= 12 ? 'pm' : 'am';
                    const minutes = d.getMinutes() > 0 ? ':' + d.getMinutes() : '';
                    const dayOfWeek = [
                        'Sunday',
                        'Monday',
                        'Tuesday',
                        'Wednesday',
                        'Thursday',
                        'Friday',
                        'Saturday',
                    ];
                    const months = [
                        'Jan',
                        'Feb',
                        'Mar',
                        'Apr',
                        'May',
                        'Jun',
                        'Jul',
                        'Aug',
                        'Sep',
                        'Oct',
                        'Nov',
                        'Dec',
                    ];
                    const ordinals = [
                        'th',
                        'st',
                        'nd',
                        'rd',
                        'th',
                        'th',
                        'th',
                        'th',
                        'th',
                        'th',
                        'th',
                        'th',
                        'th',
                        'th',
                        'th',
                        'th',
                        'th',
                        'th',
                        'th',
                        'th',
                        'th',
                        'st',
                        'nd',
                        'rd',
                        'th',
                        'th',
                        'th',
                        'th',
                        'th',
                        'th',
                        'th',
                        'st',
                    ];
                    const dateString = hours + minutes + am_pm + ' on ' + dayOfWeek[d.getDay()] + ', ' + months[d.getMonth()] + ' ' + d.getDate() + ordinals[d.getDate()] + ' ' + d.getFullYear();
                    deadline.innerHTML = '<b>Application Deadline: </b>' + dateString;
                } else {
                    deadline.innerHTML = '';
                }

                console.log( result.acf );

                if ( result.acf.application_url ) {
                    url.setAttribute( 'href', result.acf.application_url );
                    url.setAttribute( 'style', 'display:inline-block;' );
                }

                if ( result.acf.opportunity_description ) {
                    description.innerHTML = '<h3>Opportunity Description</h3><div>' + result.acf.opportunity_description + '</div>';
                } else {
                    description.innerText = '';
                }

                if ( result.acf.additional_information ) {
                    add_info.innerHTML = result.acf.additional_information;
                } else {
                    add_info.innerText = '';
                }

                if ( result.acf.location ) {
                    location.innerHTML = '<h3>Location</h3><div>' + result.acf.location.replace( '\r\n', '<br>' ) + '</div>';
                    directions.setAttribute( 'href', 'https://www.google.com/maps/dir/' + htmlDecode( result.acf.location ).replace( ' ', '+' ) );
                    directions.setAttribute( 'style', 'display:inline-block;' );
                } else {
                    location.innerText = '';
                }
                
                this.dataset.loaded = true;
            });
        });

        opportunity_dialog.on('hide', function (event) {
            activeA11yToggle();
            const image        = opportunity.querySelector( '.wr-modal--image.desktop' );
            const imageMobile  = opportunity.querySelector( '.wr-modal--image.mobile' );
            const title        = opportunity.querySelector( '.wr-modal--title' );
            const commitment   = opportunity.querySelector( '.wr-modal--commitment' );
            const deadline     = opportunity.querySelector( '.wr-modal--deadline' );
            const url          = opportunity.querySelector( '.wr-modal--url' );
            const description  = opportunity.querySelector( '.wr-modal--description' );
            const add_info     = opportunity.querySelector( '.wr-modal--additional-information' );
            const location     = opportunity.querySelector( '.wr-modal--location' );
            const directions   = opportunity.querySelector( '.wr-modal--directions' );
            
            image.setAttribute('src', '' );
            image.setAttribute('width', '' );
            image.setAttribute('height', '' );
            image.setAttribute('alt', '' );
            image.classList.add('img-loading');

            imageMobile.setAttribute('src', '' );
            imageMobile.setAttribute('width', '' );
            imageMobile.setAttribute('height', '' );
            imageMobile.setAttribute('alt', '' );
            imageMobile.classList.add('img-loading');

            title.innerHTML = '<span class="placeholder loading"></span>';
            commitment.innerHTML = '<span class="placeholder loading"></span>';
            deadline.setAttribute( 'href', 'mailto:' );
            url.setAttribute( 'href', '#!' );
            url.setAttribute( 'style', 'display:none;' );
            description.innerHTML = '<span class="placeholder loading large"></span>';
            add_info.innerHTML = '<span class="placeholder loading large"></span>';
            location.innerHTML = '<span class="placeholder loading large"></span>';
            directions.setAttribute( 'href', 'https://www.google.com/maps/dir/' );
            directions.setAttribute( 'style', 'display:none;' );

            this.dataset.loaded = false;
        });
    }

    
    // internship modal, visible with programs listings
    const internship = document.querySelector('#internship-modal');
    if ( internship ) {
        
        // disable permalinks on programs
        const internshipLinks = document.querySelectorAll( '.internship-block--link' );
        if ( internshipLinks ) {
            internshipLinks.forEach( (link) => {
                link.addEventListener( 'click', (e) => {
                    e.preventDefault();
                });
            });
        }
        
        const internship_dialog = new A11yDialog(internship);

        internship_dialog.on( 'show', function(event) {
            activeA11yToggle();
            const target = event.detail.target;
            const opener = target.closest('[data-a11y-dialog-show]');

            const image       = internship.querySelector( '.wr-modal--image.desktop' );
            const imageMobile = internship.querySelector( '.wr-modal--image.mobile' );
            const title       = internship.querySelector( '.wr-modal--title' );
            const commitment  = internship.querySelector( '.wr-modal--commitment' );
            const deadline    = internship.querySelector( '.wr-modal--deadline' );
            const button      = internship.querySelector( '.wr-modal--apply-button' );
            const description = internship.querySelector( '.wr-modal--description' );
            const add_info    = internship.querySelector( '.wr-modal--additional-information' );
            
            WPRest.get( '/wp-json/wp/v2/internship/' + opener.dataset.internshipId + '?_embed' ).then( result => {

                if ( result.featured_media ) {
                    let desktopImageSrc    = '';
                    let desktopImageWidth  = '';
                    let desktopImageHeight = '';

                    if ( result._embedded['wp:featuredmedia']['0'].media_details.sizes['image-grid'] ) {
                        let currentImage = result._embedded['wp:featuredmedia']['0'].media_details.sizes['image-grid'];
                        desktopImageSrc    = currentImage.source_url;
                        desktopImageWidth  = currentImage.width;
                        desktopImageHeight = currentImage.height;
                    } else {
                        let currentImage = result._embedded['wp:featuredmedia']['0'];
                        desktopImageSrc    = currentImage.source_url;
                        desktopImageWidth  = currentImage.media_details.width;
                        desktopImageHeight = currentImage.media_details.height;
                    }

                    let mobileImageSrc    = '';
                    let mobileImageWidth  = '';
                    let mobileImageHeight = '';
                    if ( result._embedded['wp:featuredmedia']['0'].media_details.sizes.people ) {
                        let currentImage = result._embedded['wp:featuredmedia']['0'].media_details.sizes.people;
                        mobileImageSrc    = currentImage.source_url;
                        mobileImageWidth  = currentImage.width;
                        mobileImageHeight = currentImage.height;
                    } else {
                        let currentImage = result._embedded['wp:featuredmedia']['0'];
                        mobileImageSrc    = currentImage.source_url;
                        mobileImageWidth  = currentImage.media_details.width;
                        mobileImageHeight = currentImage.media_details.height;
                    }

                    image.setAttribute('src', desktopImageSrc );
                    image.setAttribute('width', desktopImageWidth );
                    image.setAttribute('height', desktopImageHeight );
                    image.setAttribute('alt', htmlDecode( result.title.rendered ) );
                    image.classList.remove('img-loading');
                    
                    imageMobile.setAttribute('src', mobileImageSrc );
                    imageMobile.setAttribute('width', mobileImageWidth );
                    imageMobile.setAttribute('height', mobileImageHeight );
                    imageMobile.setAttribute('alt', htmlDecode( result.title.rendered ) );
                    imageMobile.classList.remove('img-loading');
                }
                
                title.innerText = htmlDecode( result.title.rendered );
                
                if ( result.acf.time_commitment ) {
                    commitment.innerHTML = '<b>Time Commitment: </b>' + htmlDecode( result.acf.time_commitment );
                } else {
                    commitment.innerText = '';
                }

                if ( result.acf.application_deadline ) {
                    const d = new Date( htmlDecode( result.acf.application_deadline ) );
                    // Friday, Feb 6th 2026
                    const dayOfWeek = [
                        'Sunday',
                        'Monday',
                        'Tuesday',
                        'Wednesday',
                        'Thursday',
                        'Friday',
                        'Saturday',
                    ];
                    const months = [
                        'Jan',
                        'Feb',
                        'Mar',
                        'Apr',
                        'May',
                        'Jun',
                        'Jul',
                        'Aug',
                        'Sep',
                        'Oct',
                        'Nov',
                        'Dec',
                    ];
                    const ordinals = [
                        'th',
                        'st',
                        'nd',
                        'rd',
                        'th',
                        'th',
                        'th',
                        'th',
                        'th',
                        'th',
                        'th',
                        'th',
                        'th',
                        'th',
                        'th',
                        'th',
                        'th',
                        'th',
                        'th',
                        'th',
                        'th',
                        'st',
                        'nd',
                        'rd',
                        'th',
                        'th',
                        'th',
                        'th',
                        'th',
                        'th',
                        'th',
                        'st',
                    ];
                    const dateString = dayOfWeek[d.getDay()] + ', ' + months[d.getMonth()] + ' ' + d.getDate() + ordinals[d.getDate()] + ' ' + d.getFullYear();
                    deadline.innerHTML = '<b>Application Deadline: </b>' + dateString;
                } else {
                    deadline.innerHTML = '';
                }

                if ( result.acf.position_summary ) {
                    description.innerHTML = '<h3>Position Summary</h3><div>' + result.acf.position_summary + '</div>';
                } else {
                    description.innerText = '';
                }

                if ( result.acf.additional_information ) {
                    add_info.innerHTML = result.acf.additional_information;
                } else {
                    add_info.innerText = '';
                }

                if ( result.acf.application_url ) {
                    button.setAttribute( 'href', result.acf.application_url );
                    button.setAttribute( 'style', 'display:inline-block;' );
                }

                this.dataset.loaded = true;
            });            
        });
        
        internship_dialog.on('hide', function (event) {
            activeA11yToggle();
            const image       = internship.querySelector( '.wr-modal--image.desktop' );
            const imageMobile = internship.querySelector( '.wr-modal--image.mobile' );
            const title       = internship.querySelector( '.wr-modal--title' );
            const commitment  = internship.querySelector( '.wr-modal--commitment' );
            const deadline    = internship.querySelector( '.wr-modal--deadline' );
            const button      = internship.querySelector( '.wr-modal--apply-button' );
            const description = internship.querySelector( '.wr-modal--description' );
            const add_info    = internship.querySelector( '.wr-modal--additional-information' );
            
            image.setAttribute('src', '' );
            image.setAttribute('width', '' );
            image.setAttribute('height', '' );
            image.setAttribute('alt', '' );
            image.classList.add('img-loading');

            imageMobile.setAttribute('src', '' );
            imageMobile.setAttribute('width', '' );
            imageMobile.setAttribute('height', '' );
            imageMobile.setAttribute('alt', '' );
            imageMobile.classList.add('img-loading');

            title.innerHTML = '<span class="placeholder loading"></span>';
            commitment.innerHTML = '<span class="placeholder loading"></span>';
            deadline.innerHTML = '<span class="placeholder loading"></span>';
            button.setAttribute( 'href', '#!' );
            button.innerText = "Apply Now";
            button.setAttribute( 'target', '_self' );
            button.setAttribute( 'style', 'display:none;' );
            description.innerHTML = '<span class="placeholder loading large"></span>';
            add_info.innerHTML = '<span class="placeholder loading large"></span>';
            button.setAttribute( 'href', '#' );
            button.setAttribute( 'style', 'display:none;' );

            this.dataset.loaded = false;
        });
    }
    
	function htmlDecode(input) {
		var doc = new DOMParser().parseFromString(input, "text/html");
		return doc.documentElement.textContent;
	}
}
